<app-standard-page-content>
  <app-standard-page-content-actions>
    <h4>
      <strong>{{ regimeName$ | async }}</strong> Transaction History for
      {{ period$ | async | aadaterange }}
    </h4>

    <div class="row">
      <div class="col-sm-6 col-xl-4">
        <div class="row gx-2 mb-3">
          <div class="col-6 text-body-secondary">
            Total <abbr title="Depreciation">Depn.</abbr>
            <div class="text-sm">
              (up to {{ periodEndDate$ | async | aadate }})
            </div>
          </div>
          <div class="col-6">
            {{ depreciation | aacurrency }}
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-4">
        <div class="row gx-2 mb-3">
          <div class="col-6 text-body-secondary">
            <abbr title="Carrying Value (NBV)">CV</abbr>
            <div class="text-sm">
              (at {{ periodEndDate$ | async | aadate }})
            </div>
          </div>
          <div class="col-6">
            {{ writtenDownValue | aacurrency }}
          </div>
        </div>
      </div>
    </div>
  </app-standard-page-content-actions>

  @if (showGrid) {
    <app-standard-page-content-grid>
      <div class="horizontal-wrap justify-content-start">
        <div class="me-2 mb-1">
          <app-grid-expand-collapse-button
            #expandCollapseButton
            key="asset-transactions"
          ></app-grid-expand-collapse-button>
        </div>

        <div class="btn-group me-2 mb-1">
          <input
            data-dd-action-name="view-asset-transactions.reversals-radio.show"
            id="includeReversals-input"
            type="radio"
            class="btn-check"
            [value]="true"
            [(ngModel)]="includeReversals"
            [disabled]="!hasReversals"
          />
          <label
            id="includeReversals"
            for="includeReversals-input"
            class="btn btn-outline btn-sm mb-0"
          >
            Show Reversals
          </label>

          <input
            data-dd-action-name="view-asset-transactions.reversals-radio.hide"
            id="excludeReversals-input"
            type="radio"
            class="btn-check"
            [value]="false"
            [(ngModel)]="includeReversals"
            [disabled]="!hasReversals"
          />
          <label
            id="excludeReversals"
            for="excludeReversals-input"
            class="btn btn-outline btn-sm mb-0"
          >
            Hide Reversals
          </label>
        </div>
        <div class="ml-auto">
          <app-export-button
            #exportButton
            [gridExportParams]="gridExportParams"
            size="small"
          ></app-export-button>
        </div>
      </div>

      <app-grid-table
        [rowData]="rowData"
        [rowHeight]="getRowHeight"
        [rowClassRules]="rowClassRules"
        [isRowGroupOpenByDefault]="isRowGroupOpenByDefault"
        [expandCollapseButton]="expandCollapseButton"
        [exportButton]="exportButton"
        class="row-grouping-single-column"
      >
        <app-grid-column
          field="openingBalanceLabel"
          [hide]="true"
          [rowGroup]="!hideGrouping"
        >
        </app-grid-column>

        <app-grid-column
          field="financialYear"
          [hide]="true"
          [rowGroup]="!hideGrouping"
        >
        </app-grid-column>

        <app-grid-column
          headerName="Date"
          field="date"
          fixed="left"
          cellClass="text-nowrap"
          [maxWidth]="220"
        >
          <ng-template let-value>{{ value | aadate }}</ng-template>
        </app-grid-column>

        <app-grid-column
          headerName="Type"
          field="type"
          [getValue]="getTransactionTypeCustomGrid"
        ></app-grid-column>

        @if (showPoolAsset) {
          <app-grid-column
            headerName="Asset"
            colId="poolAssetId"
            field="poolAssetId"
            [width]="200"
            [grow]="3"
          >
            <ng-template let-value let-data="data">
              <div class="Col--asset">
                @if (value) {
                  <a
                    data-dd-action-name="view-asset-transactions.pool-asset-link"
                    [routerLink]="poolAssetRoute(data)"
                    >{{ data.poolAssetName }}</a
                  >
                }
              </div>
            </ng-template>
          </app-grid-column>
        }

        <app-grid-column-group
          groupId="AssetGroup"
          headerName="Asset"
          [defaultOpen]="true"
        >
          <app-grid-column
            headerName="Cost"
            field="cost"
            valueFormat="currency"
            columnGroupShow="open"
            cellClass="text-end"
            aggFunc="sum"
          >
          </app-grid-column>

          <!-- Show total in header -->
          <app-grid-column
            headerName="Depreciation"
            field="depreciation"
            valueFormat="currency"
            columnGroupShow="open"
            cellClass="text-end"
            aggFunc="sum"
          >
          </app-grid-column>

          <!-- Show total in header -->
          @if (showTaxableDepreciation) {
            <app-grid-column
              headerName="Taxable Depreciation"
              headerAbbreviation="Taxable Depn"
              field="taxableDepreciation"
              valueFormat="currency"
              columnGroupShow="open"
              cellClass="text-end"
              aggFunc="sum"
            >
            </app-grid-column>
          }

          <!-- cellRenderer="aaHideExpandedCellRenderer" -->
          <app-grid-column
            headerName="Tax Value"
            field="writtenDownValue"
            fixed="right"
            valueFormat="currency"
            cellClass="text-end"
            aggFunc="last"
          >
          </app-grid-column>
        </app-grid-column-group>

        <app-grid-column
          headerName="Description"
          field="descriptionAndSource"
          [getValue]="getTransactionDescriptionAndSource"
          [grow]="3"
        >
          <ng-template let-data="data">
            <app-transaction-description-cell
              [data]="data"
            ></app-transaction-description-cell>
          </ng-template>
        </app-grid-column>

        <app-grid-column
          headerName="Description"
          field="description"
          [getValue]="getTransactionDescription"
          [hide]="true"
        >
        </app-grid-column>

        <app-grid-column
          headerName="Source"
          field="source"
          [getValue]="getTransactionSource"
          [hide]="true"
        >
        </app-grid-column>
      </app-grid-table>

      @if (areMoreTransactionsAvailable) {
        <div>
          <button
            data-dd-action-name="view-asset-transactions.show-more-button"
            type="button"
            class="btn btn-sm btn-outline mb-3"
            [disabled]="transactionsLoading"
            (click)="onGetMoreTransactions()"
          >
            <i-lucide name="plus-circle"></i-lucide>
            Show More Transactions
          </button>
        </div>
      }
    </app-standard-page-content-grid>
  }
</app-standard-page-content>
